import axios, { AxiosResponse } from 'axios';
import { ApiResponse, User, LoginData, RegisterData } from '../types';

// Base URL logic:
// - In production we set VITE_API_URL=/api (nginx proxies /api -> backend)
// - In development (no env) we fall back to http://localhost:3004/api
// Ensure base ends with /api so route definitions can omit the /api prefix.
const rawEnvBase = import.meta.env.VITE_API_URL as string | undefined;
let API_BASE_URL = rawEnvBase || 'http://localhost:3004/api';
if (!API_BASE_URL.endsWith('/api')) {
  // If the provided env is domain root, append /api
  API_BASE_URL = API_BASE_URL.replace(/\/$/, '') + '/api';
}

export const getBaseUrl = () => API_BASE_URL;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor: add auth token; skip Content-Type for FormData (browser must set multipart/form-data + boundary)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Import useAuthStore dynamically to avoid circular dependency
      import('../store/authStore').then(({ useAuthStore }) => {
        // Clear auth state in Zustand store
        const { logout } = useAuthStore.getState();
        logout();
      }).catch(() => {
        // Fallback: just clear localStorage if import fails
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      });

      // Avoid redirect loops: only navigate if not already on the login page
      const current = window.location.pathname;
      if (current !== '/login' && current !== '/register') {
        // Use a small delay to ensure state is cleared before redirect
        setTimeout(() => {
          window.location.href = '/login';
        }, 100);
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (data: LoginData): Promise<AxiosResponse<ApiResponse<{ user: User; token: string }>>> =>
    api.post('/auth/login', data),
  
  register: (data: RegisterData): Promise<AxiosResponse<ApiResponse<{ user: User; token: string }>>> =>
    api.post('/auth/register', data),
  
  logout: (): Promise<AxiosResponse<ApiResponse<null>>> =>
    api.post('/auth/logout'),
  
  me: (): Promise<AxiosResponse<ApiResponse<{ user: User; profile: Record<string, unknown> | null }>>> =>
    api.get('/auth/me'),
  
  verifyEmail: (token: string): Promise<AxiosResponse<ApiResponse<null>>> =>
    api.post('/auth/verify-email', { token }),

  resendVerification: (email: string): Promise<AxiosResponse<ApiResponse<null>>> =>
    api.post('/auth/resend-verification', { email }),

  forgotPassword: (email: string): Promise<AxiosResponse<ApiResponse<null>>> =>
    api.post('/auth/forgot-password', { email }),
  
  resetPassword: (token: string, password: string): Promise<AxiosResponse<ApiResponse<null>>> =>
    api.post('/auth/reset-password', { token, password }),
};

export const vacancyAPI = {
  getAll: (params?: { 
    page?: number; 
    limit?: number; 
    status?: string; 
    search?: string;
    professional_type?: string;
    work_preference?: string;
    active_regions?: string | string[];
  }): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get('/professional/vacancies', { params }),
  
  getById: (id: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get(`/professional/vacancies/${id}`),
  
  apply: (id: string, motivation?: string): Promise<AxiosResponse<ApiResponse<null>>> =>
    api.post(`/professional/vacancies/${id}/accept`, { motivation }),
};

export const opdrachtenAPI = {
  // Professional endpoints
  getByStatus: (status?: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get('/professional/opdrachten', { params: { status } }),
  
  negotiate: (id: string, changes: any): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(`/professional/opdrachten/${id}/negotiate`, { changes }),
  
  approveChange: (id: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(`/professional/opdrachten/${id}/approve-change`),
  
  complete: (id: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(`/professional/opdrachten/${id}/complete`),
  
  approveCompletion: (id: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(`/professional/opdrachten/${id}/approve-completion`),
  
  reject: (id: string, reason?: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(`/professional/opdrachten/${id}/reject`, { reason }),
  
  approveRejection: (id: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(`/professional/opdrachten/${id}/approve-rejection`),
};

export const employerOpdrachtenAPI = {
  // Employer endpoints
  getByStatus: (status?: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get('/employer/opdrachten', { params: { status } }),
  
  negotiate: (id: string, professionalId: string, changes: any): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(`/employer/opdrachten/${id}/negotiate`, { professional_id: professionalId, changes }),
  
  approveChange: (id: string, professionalId: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(`/employer/opdrachten/${id}/approve-change`, { professional_id: professionalId }),
  
  complete: (id: string, professionalId: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(`/employer/opdrachten/${id}/complete`, { professional_id: professionalId }),
  
  approveCompletion: (id: string, professionalId: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(`/employer/opdrachten/${id}/approve-completion`, { professional_id: professionalId }),
  
  reject: (id: string, professionalId: string, reason?: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(`/employer/opdrachten/${id}/reject`, { professional_id: professionalId, reason }),
  
  approveRejection: (id: string, professionalId: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(`/employer/opdrachten/${id}/approve-rejection`, { professional_id: professionalId }),
};

export default api;
